import { computed } from 'vue'
import { usePermissions } from './usePermissions'
import type { ResourceType } from '@/types/roles'
import { canAccessPath as checkPathAccess } from '@/utils/permissions'

/**
 * Field-level permission evaluation composable
 * 
 * Provides path-based permission checking for configuration fields,
 * enabling granular control over which properties users can edit.
 * 
 * @example
 * ```ts
 * const { canEditField, getFieldPermission } = useFieldPermissions()
 * 
 * // Check if user can edit a specific field
 * if (canEditField('tenants', 'Licensing')) {
 *   // Show editable input
 * } else {
 *   // Show readonly input with lock icon
 * }
 * 
 * // Get full permission info for a field
 * const permission = getFieldPermission('tenants', 'database.connectionString')
 * console.log(permission.canEdit) // true/false
 * console.log(permission.reason) // 'No write access to path: database.connectionString'
 * ```
 */
export function useFieldPermissions() {
  const permissions = usePermissions()

  /**
   * Check if user can edit a specific field/path in a resource
   * 
   * @param resource - Resource type (e.g., 'presets', 'tenants')
   * @param path - JSON path to the field (e.g., 'Licensing', 'database.connectionString')
   * @returns True if user can edit the field
   */
  function canEditField(resource: ResourceType, path: string): boolean {
    if (!permissions.isLoaded.value) return false
    return checkPathAccess(permissions.scopes.value, resource, path)
  }

  /**
   * Check if user can edit any field in a resource
   * 
   * @param resource - Resource type to check
   * @returns True if user has any write access to the resource
   */
  function canEditAnyField(resource: ResourceType): boolean {
    return permissions.canWrite(resource)
  }

  /**
   * Check if user has full edit access to a resource (no path restrictions)
   * 
   * @param resource - Resource type to check
   * @returns True if user can edit all fields
   */
  function hasFullEditAccess(resource: ResourceType): boolean {
    if (!permissions.isLoaded.value) return false
    
    // Check if user has wildcard scope for resource (e.g., 'tenants:write' or 'tenants:*')
    const scopes = permissions.scopes.value
    const hasWildcardWrite = scopes.some(scope => {
      const parts = scope.split(':')
      if (parts.length < 2) return false
      
      const [scopeResource, scopeOperation] = parts
      return (scopeResource === resource || scopeResource === '*') &&
             (scopeOperation === 'write' || scopeOperation === '*') &&
             parts.length === 2 // No path specified = full access
    })
    
    return hasWildcardWrite
  }

  /**
   * Get detailed permission information for a field
   * 
   * @param resource - Resource type
   * @param path - JSON path to the field
   * @returns Permission info with canEdit flag and reason
   */
  function getFieldPermission(resource: ResourceType, path: string): {
    canEdit: boolean
    isReadOnly: boolean
    reason: string | null
  } {
    if (!permissions.isLoaded.value) {
      return {
        canEdit: false,
        isReadOnly: true,
        reason: 'Permissions not loaded'
      }
    }

    const canEdit = canEditField(resource, path)
    
    if (canEdit) {
      return {
        canEdit: true,
        isReadOnly: false,
        reason: null
      }
    }

    // Determine why user can't edit
    const canWriteResource = permissions.canWrite(resource)
    if (!canWriteResource) {
      return {
        canEdit: false,
        isReadOnly: true,
        reason: `No write access to ${resource}`
      }
    }

    // Has write access to resource but not this specific path
    return {
      canEdit: false,
      isReadOnly: true,
      reason: `No write access to path: ${path}`
    }
  }

  /**
   * Get list of editable paths for a resource based on user's scopes
   * 
   * @param resource - Resource type
   * @returns Array of paths user can edit, or null if all paths are editable
   */
  function getEditablePaths(resource: ResourceType): string[] | null {
    if (!permissions.isLoaded.value) return []
    
    const scopes = permissions.scopes.value
    const editablePaths: string[] = []
    let hasFullAccess = false

    for (const scope of scopes) {
      const parts = scope.split(':')
      if (parts.length < 2) continue

      const [scopeResource, scopeOperation, scopePath] = parts
      
      // Check if scope applies to this resource
      if (scopeResource !== resource && scopeResource !== '*') continue
      
      // Check if scope grants write permission
      if (scopeOperation !== 'write' && scopeOperation !== '*') continue

      // No path specified = full access
      if (!scopePath) {
        hasFullAccess = true
        break
      }

      editablePaths.push(scopePath)
    }

    // null means all paths are editable
    if (hasFullAccess) return null
    
    return editablePaths
  }

  /**
   * Check if a field should show a lock icon (readonly due to permissions)
   * 
   * @param resource - Resource type
   * @param path - JSON path to the field
   * @returns True if field should show lock icon
   */
  function shouldShowLockIcon(resource: ResourceType, path: string): boolean {
    // Only show lock if user has some write access to resource but not this path
    const canWriteResource = permissions.canWrite(resource)
    if (!canWriteResource) return false // No write access at all - don't show lock on every field
    
    const canEdit = canEditField(resource, path)
    return !canEdit // Has write access but can't edit this path
  }

  /**
   * Batch check permissions for multiple fields
   * Useful for optimizing permission checks in large forms
   * 
   * @param resource - Resource type
   * @param paths - Array of field paths to check
   * @returns Map of path -> canEdit
   */
  function checkMultipleFields(
    resource: ResourceType,
    paths: string[]
  ): Map<string, boolean> {
    const result = new Map<string, boolean>()
    
    if (!permissions.isLoaded.value) {
      paths.forEach(path => result.set(path, false))
      return result
    }

    // Optimize: if user has full access, all fields are editable
    if (hasFullEditAccess(resource)) {
      paths.forEach(path => result.set(path, true))
      return result
    }

    // Check each path individually
    paths.forEach(path => {
      result.set(path, canEditField(resource, path))
    })

    return result
  }

  /**
   * Check if user should see a field at all
   * (some fields might be hidden entirely if user has no read access)
   * 
   * @param resource - Resource type
   * @returns True if user can view fields in this resource
   */
  const canViewFields = computed(() => {
    return (resource: ResourceType) => permissions.canRead(resource)
  })

  /**
   * Check if entire resource is readonly for current user
   * 
   * @param resource - Resource type
   * @returns True if resource is readonly
   */
  const isResourceReadOnly = computed(() => {
    return (resource: ResourceType) => permissions.isReadOnlyAccess(resource)
  })

  return {
    // Basic checks
    canEditField,
    canEditAnyField,
    hasFullEditAccess,
    
    // Detailed info
    getFieldPermission,
    getEditablePaths,
    
    // UI helpers
    shouldShowLockIcon,
    
    // Optimization
    checkMultipleFields,
    
    // Computed helpers
    canViewFields,
    isResourceReadOnly
  }
}
